import { FormikHandlers, useFormikContext } from "formik";
import React from "react";

import { Keys } from "@utils/enums";
import { padStart } from "@utils/textUtils";

import {
    BaseField,
    BaseProps,
} from "@components/Form/FormFields/Base/base.component";
import { Input } from "@components/General/Form/input.component";

type TextFieldProps = BaseProps & {
    innerRef?: React.RefObject<HTMLInputElement>;
    className?: string;
    placeholder?: string;
    type?: "text" | "textarea" | "number";
    forceError?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    pattern?: string;
    autoComplete?: string;
    maxLength?: number;
    prependWith?: string;
    autoTabTo?: React.RefObject<HTMLInputElement>;
    autoTabFrom?: React.RefObject<HTMLInputElement>;
    parseToString?: boolean;
    strictInteger?: boolean;
    strictPattern?: boolean;
    disableValidation?: boolean;
    test?: string;
    "aria-label"?: string;
};

const TextField = ({
    label,
    name,
    microCopy,
    required,
    hideLabel,
    optionalIndication,
    innerRef,
    className,
    placeholder,
    type,
    forceError,
    disabled,
    readOnly,
    pattern,
    autoComplete,
    validationRule,
    disableValidation,
    maxLength,
    prependWith = "",
    autoTabFrom,
    autoTabTo,
    parseToString,
    strictInteger,
    strictPattern,
    test,
    debounce,
    hideMicroCopy,
    addon,
    tooltip,
    "aria-label": ariaLabel,
}: TextFieldProps) => {
    const { setFieldValue, setFieldTouched, validateField } =
        useFormikContext();

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
        onBlurCB: FormikHandlers["handleBlur"],
    ) => {
        const { value = "" } = e.currentTarget;

        if (maxLength && prependWith && value && value.length < maxLength) {
            setTimeout(() => {
                setFieldValue(name, padStart(value, maxLength, prependWith));
                setFieldTouched(name, true, true);
            });
            e.preventDefault();
            return;
        }

        onBlurCB && onBlurCB(e);
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { value = "" } = e.currentTarget;
        if (
            value.length === 0 &&
            e.keyCode === Keys.BACKSPACE &&
            autoTabFrom?.current
        ) {
            autoTabFrom.current.focus();
        }

        if (
            maxLength &&
            prependWith &&
            e.keyCode === Keys.TAB &&
            value &&
            value.length < maxLength
        ) {
            setTimeout(() => {
                setFieldValue(
                    name,
                    padStart(value, maxLength, prependWith),
                    true,
                );
            });
        }
    };

    const handleKeyUp = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { value = "" } = e.currentTarget;

        const ignoredKeys = [
            Keys.ARROW_DOWN,
            Keys.ARROW_UP,
            Keys.TAB,
            Keys.SHIFT,
            Keys.CTRL,
            Keys.ALT,
        ];

        if (ignoredKeys.includes(e.keyCode)) {
            return;
        }

        if (maxLength && value.length === maxLength) {
            autoTabTo?.current?.focus();
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChangeCB: FormikHandlers["handleChange"],
        oldValue: string,
    ) => {
        const { value = "", validity } = e.currentTarget;
        let parsedValue = value;
        if (parseToString) {
            parsedValue = value.trim().toString();
        }

        // enforce pattern
        if (
            strictPattern &&
            pattern &&
            parsedValue &&
            !parsedValue.match(new RegExp(pattern))
        ) {
            e.preventDefault();
            return;
        }

        if (strictInteger && !parsedValue.match(/^[0-9]*$/)) {
            // als het enkel gehele getallen mogen zijn => strictInteger
            e.preventDefault();
            return;
        }

        // browser reset the field due to invalid input ( eg. input with '-')
        if (strictInteger && !parsedValue && !!validity.badInput) {
            e.preventDefault();
            setTimeout(() => setFieldValue(name, oldValue));
            return;
        }

        // als maxlength voorbij is, geen input meer toelaten
        const hasPassedMaxLength =
            maxLength &&
            maxLength > 0 &&
            parsedValue.toString().length > maxLength;
        if (hasPassedMaxLength) {
            e.preventDefault();
            return;
        }

        // Use this for large numbers eg. gift card
        // Gift card number exceeds MAX_SAFE_INTEGER, 29008389902072765 becomes 29008389902072764
        // formik parses the value to an integer as default when field type is set to number
        if (parseToString) {
            setFieldValue(name, parsedValue);
        } else {
            onChangeCB && onChangeCB(e);
        }

        const equalMaxLength =
            maxLength &&
            maxLength > 0 &&
            parsedValue.toString().length === maxLength;

        if (equalMaxLength && autoTabTo?.current) {
            setTimeout(() => {
                validateField(name);
            });
        }
    };

    return (
        <BaseField
            label={label}
            name={name}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
            optionalIndication={optionalIndication}
            validationRule={validationRule}
            debounce={debounce}
            addon={addon}
            tooltip={tooltip}
        >
            {({ field, meta }) => {
                return (
                    <Input
                        data-test={test}
                        {...field}
                        innerRef={innerRef}
                        onBlur={e => handleBlur(e, field.onBlur)}
                        onKeyDown={handleKeyDown}
                        onKeyUp={handleKeyUp}
                        className={className}
                        id={name}
                        type={type && !strictInteger ? type : "text"}
                        placeholder={placeholder ? placeholder : ""}
                        autoComplete={autoComplete}
                        valid={
                            !disableValidation &&
                            !forceError &&
                            meta.touched &&
                            !meta.error
                        }
                        invalid={
                            !disableValidation &&
                            (forceError || (meta.touched && !!meta.error))
                        }
                        onChange={e =>
                            handleChange(e, field.onChange, field.value)
                        }
                        pattern={pattern}
                        disabled={disabled}
                        readOnly={readOnly}
                        aria-label={ariaLabel}
                    />
                );
            }}
        </BaseField>
    );
};

export { TextField };
